import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { formatDate } from 'date-fns'
import { format } from 'd3-format'
import { ReactNode } from 'react'
import clsx from 'clsx'

/**
 * Renders a boolean value as a checkmark or close icon
 */
export const RenderBoolean = ({ value }: { value: boolean }): ReactNode => {
  return (
    <div className="pl-3">
      {value ? (
        <IconCheckMarkOutline className="text-green-800" size={12} />
      ) : (
        <IconCloseOutline className="text-red-800" size={12} />
      )}
    </div>
  )
}

/**
 * Renders a date or datetime value
 */
export const RenderDate = ({ value, formatter }: { value: string; formatter?: string }): ReactNode => {
  const dateFormat = formatter || 'MM/dd/yyyy'
  return formatDate(new Date(value), dateFormat)
}

/**
 * Renders a number value with optional formatting
 */
export const RenderNumber = ({ value, formatter }: { value: number; formatter?: string }): ReactNode => {
  if (!formatter) {
    return value
  }
  if (isNaN(value)) {
    return value
  }
  return format(formatter)(value)
}

/**
 * Renders a string with optional truncation and tooltip
 */
export const RenderString = ({
  value,
  shouldTruncate,
  maxWidth,
}: {
  value: string
  shouldTruncate?: boolean
  maxWidth?: number
}): ReactNode => {
  const isLink = value.startsWith('http')
  const style = maxWidth ? { maxWidth: maxWidth } : undefined
  if (!shouldTruncate) {
    const className = clsx('line-clamp-2 inline-block cursor-context-menu', { 'cursor-pointer': isLink })
    return (
      <TooltipButton tooltip={value} side="left" contentClassName="text-sm p-5">
        {isLink ? (
          <a href={value} style={style} className={className} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          <span style={style} className={className}>
            {value}
          </span>
        )}
      </TooltipButton>
    )
  }

  const className = clsx('inline-block cursor-context-menu truncate', { 'cursor-pointer': isLink })
  return (
    <TooltipButton tooltip={value} side="left" contentClassName="text-sm p-5">
      <span style={style} className={className}>
        {isLink ? (
          <a href={value} style={style} className={className} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          <span style={style} className={className}>
            {value}
          </span>
        )}
      </span>
    </TooltipButton>
  )
}

/**
 * Renders a string with an icon prefix
 */
export const RenderStringWithIcon = ({ value, iconName }: { value: string; iconName: string }): ReactNode => {
  return (
    <div className="flex items-center gap-2">
      <DynamicIcon size={16} iconName={iconName} />
      {value}
    </div>
  )
}
